import { call, put, select, delay } from 'redux-saga/effects'
import { Action } from 'infra/types'
import { IAppState } from 'infra/AppState'
import { ENTITY_TYPE, API_RESPONSE_TYPE } from 'shared/constants'
import { Mixpanel } from 'services/Tracking/Mixpanel'
import { getTransactionDatabaseManager } from 'services/IndexDB/TransactionDatabaseManager'
import { getTransactionsForLoginApi, getTransactionFileApi } from 'services/Api/StafflinkApi'
import { ITransactionFileData } from 'services/Api/types'
import {
  SEED_VERSION,
  SEED_ACCOUNT_TYPES,
  SEED_ACCOUNT_TYPE,
  SEED_FILE_POLL,
  seedAccountTypeToEntityType
} from 'services/TransactionSync/seed/seedConfig'
import { refreshMerchantTransactions } from 'state/transaction/transaction.actions'
import {
  ISeedTriggerPayload,
  seedStarted,
  seedPhase,
  seedSuccess,
  seedFailure,
  skipSeed
} from './seed.action'

interface ITransactionFileRef {
  url: string
  encryption_key: string
}

/**
 * Lightweight capability check done BEFORE loading the (heavy) decoder chunk, so
 * unsupported browsers skip the seed without pulling protobufjs/fflate. Mirrors
 * `isDecoderSupported` in the decoder module.
 */
const isSeedSupported = (): boolean =>
  typeof window !== 'undefined' &&
  !!window.indexedDB &&
  typeof crypto !== 'undefined' &&
  !!crypto.subtle &&
  typeof fetch !== 'undefined'

/** Lazy chunk: protobufjs + fflate + decoder load only when a seed actually runs. */
const loadDecoder = () =>
  import(/* webpackChunkName: "txn-seed" */ '../../services/TransactionSync/decoder')

/**
 * Businesses whose seed is running in THIS tab — dedups concurrent triggers (a
 * double selectBusinessAccount dispatch, a Retry fired while one is mid-flight).
 */
const inFlightSeeds = new Set<string>()

/**
 * Bumped on signout so an in-flight (background) seed stops writing — otherwise it
 * could resurrect data right after signout wipes IndexedDB. Each seed captures the
 * epoch at start and bails if it changes.
 */
let seedEpoch = 0

export const cancelSeeds = (): void => {
  seedEpoch += 1
  inFlightSeeds.clear()
}

/**
 * Exclusive CROSS-TAB lock per business (Web Locks API). Open the app in 5 tabs
 * and only ONE actually seeds a given business; the others wait on the lock and
 * then find it already done. Resolves once the lock is held and returns a release
 * fn. Falls back to a no-op lock where the API is unavailable (no cross-tab dedup).
 */
const acquireSeedLock = (businessId: string): Promise<{ release: () => void }> => {
  const locks = typeof navigator !== 'undefined' ? (navigator as any).locks : undefined
  if (!locks || typeof locks.request !== 'function') {
    return Promise.resolve({ release: () => undefined })
  }
  return new Promise((resolveAcquired) => {
    locks
      .request(
        `okc-txn-seed:${businessId}`,
        () => new Promise<void>((releaseLock) => resolveAcquired({ release: releaseLock }))
      )
      .catch(() => resolveAcquired({ release: () => undefined }))
  })
}

/** True when the business is fully seeded at the current version (all account types done). */
function* isFullySeeded(dbManager: any, businessId: string): Generator<any, boolean, any> {
  const record = yield call([dbManager, dbManager.getSeedInfo], businessId)
  if (!record || record.seedVersion !== SEED_VERSION) {
    return false
  }
  const done = new Set(record.completedAccountTypes || [])
  return SEED_ACCOUNT_TYPES.every((accountType) => done.has(accountType))
}

/**
 * Ask the backend to export this account_type's full history and poll until the
 * signed URL + AES key are ready. Returns null when there is simply no history
 * for the type. Throws on API failure / never-ready.
 */
function* fetchTransactionFileRef(
  businessId: string,
  accountType: SEED_ACCOUNT_TYPE
): Generator<any, ITransactionFileRef | null, any> {
  const loginResp = yield call(
    getTransactionsForLoginApi,
    { account_type: accountType },
    businessId
  )
  if (loginResp?.type !== API_RESPONSE_TYPE.SUCCESS) {
    throw new Error(`GetTransactionsForLogin failed for account_type ${accountType}`)
  }
  const fileId: string | undefined = loginResp?.data?.transaction_file_id
  if (!fileId) {
    return null
  }

  // Poll until the export is ready. Mirrors Android's `fetchFileUrlForFileId`:
  // delay(min(2000, attempt * 200)) BEFORE each request, unbounded — returns only
  // when status === 1 (FILE_CREATION_COMPLETED) with a signed URL + key, or throws
  // if a request fails (→ SEED_FAILURE).
  for (let attempt = 1; ; attempt++) {
    yield delay(Math.min(SEED_FILE_POLL.maxDelayMs, attempt * SEED_FILE_POLL.stepMs))
    const fileResp = yield call(getTransactionFileApi, { transactionFileId: fileId }, businessId)
    if (fileResp?.type !== API_RESPONSE_TYPE.SUCCESS) {
      throw new Error('GetTransactionFile failed')
    }
    const txnFile: ITransactionFileData | undefined = fileResp?.data?.txn_file
    if (txnFile && txnFile.status === 1 && txnFile.file && txnFile.encryption_key) {
      return { url: txnFile.file, encryption_key: txnFile.encryption_key }
    }
  }
}

/**
 * Store a decoded file. `bulkStoreTransactions` writes ONE record per account (its
 * whole array, replacing any existing) so no per-account delete is needed. Then
 * batch-mark all accounts fully-fetched. Returns the number stored.
 */
function* storeSeededTransactions(
  dbManager: any,
  entityType: ENTITY_TYPE,
  txns: any[],
  businessId: string
): Generator<any, number, any> {
  if (!txns || txns.length === 0) {
    return 0
  }
  const accountIds = Array.from(new Set(txns.map((t: any) => t?.account_id).filter(Boolean)))
  // Tagged with the business so the delta cursor never mixes owned businesses.
  yield call([dbManager, dbManager.bulkStoreTransactions], entityType, txns, businessId)
  yield call([dbManager, dbManager.bulkSetFullFetchCompleted], accountIds, entityType)
  return txns.length
}

/**
 * Seed one business. Robust to reloads, multiple tabs, and signout:
 *  - RESUMES from a per-account_type checkpoint instead of restarting (a reload
 *    after customers are done only re-does suppliers).
 *  - Takes a CROSS-TAB lock so N tabs seed a business at most once.
 *  - Bails without writing if signed out mid-run (epoch), and wipes any partial
 *    write in the `finally`.
 *  - On any failure dispatches SEED_FAILURE without marking complete, so the app
 *    proceeds on lazy fetch and retries next boot.
 */
function* seedBusiness(businessId: string, isActive: boolean): Generator<any, void, any> {
  if (inFlightSeeds.has(businessId)) {
    return
  }
  inFlightSeeds.add(businessId)
  const epoch = seedEpoch
  const dbManager = getTransactionDatabaseManager()
  let lock: { release: () => void } = { release: () => undefined }
  try {
    yield call([dbManager, dbManager.init])

    // Already fully seeded? Skip the full re-fetch without taking the cross-tab
    // lock (fast reload path). For the ACTIVE business, kick off an incremental
    // delta sync (same as the Refresh button) so a reload picks up transactions
    // changed elsewhere since the last sync — cheap `type:0` GetTransactions from
    // the local `update_time` watermark, not a full re-download.
    if (yield call(isFullySeeded, dbManager, businessId)) {
      yield put(seedSuccess({ businessId }))
      if (isActive) {
        yield put(refreshMerchantTransactions())
      }
      return
    }
    if (!isSeedSupported()) {
      Mixpanel.track('txn_seed_skipped', { business_id: businessId, reason: 'unsupported' })
      yield put(skipSeed({ businessId }))
      return
    }

    yield put(seedStarted({ businessId, isActive }))
    Mixpanel.track('txn_seed_started', { business_id: businessId, is_active: isActive })

    // One tab at a time per business (5 tabs → 1 seed). Other tabs wait here.
    lock = yield call(acquireSeedLock, businessId)
    if (seedEpoch !== epoch) {
      return
    }
    // Another tab may have finished while we waited for the lock.
    if (yield call(isFullySeeded, dbManager, businessId)) {
      yield put(seedSuccess({ businessId }))
      return
    }

    // Resume from the checkpoint: skip account_types already completed.
    const record = yield call([dbManager, dbManager.getSeedInfo], businessId)
    const isCurrent = record && record.seedVersion === SEED_VERSION
    const completed: number[] =
      isCurrent && Array.isArray(record.completedAccountTypes)
        ? record.completedAccountTypes.slice()
        : []
    const done = new Set(completed)

    const decoder = yield call(loadDecoder)
    let processed = 0
    // Advance the stepper phase monotonically (matches Android's stepper).
    let announcedDownloading = false
    let entryCount = 0
    for (const accountType of SEED_ACCOUNT_TYPES) {
      if (done.has(accountType)) {
        continue
      }
      if (seedEpoch !== epoch) {
        return
      }
      const entityType = seedAccountTypeToEntityType(accountType)
      const fileRef: ITransactionFileRef | null = yield call(
        fetchTransactionFileRef,
        businessId,
        accountType
      )
      if (fileRef) {
        if (!announcedDownloading) {
          yield put(seedPhase({ businessId, phase: 'downloading' }))
          announcedDownloading = true
        }
        const txns = yield call(decoder.downloadAndDecodeTransactionFile, fileRef)
        if (seedEpoch !== epoch) {
          return
        }
        // Re-announced per file so the SyncScreen's entry count grows as each
        // account type is decoded; the phase itself never moves backwards.
        entryCount += txns ? txns.length : 0
        yield put(seedPhase({ businessId, phase: 'preparing', entryCount }))
        processed += yield call(storeSeededTransactions, dbManager, entityType, txns, businessId)

        // Checkpoint this account_type ONLY after we actually fetched its file, so a
        // reload resumes here instead of restarting. A type we could NOT get a file
        // for (null fileRef — e.g. the backend returned no transaction_file_id) is
        // deliberately NOT checkpointed, so it is retried on the next boot instead of
        // being permanently marked "fully seeded" with zero transactions (which, with
        // the seed then skipped forever, left ledgers empty). The empty-case backend
        // contract is unconfirmed; retrying is the safe default and only costs one
        // cheap GetTransactionsForLogin call. Types that DID seed are already
        // checkpointed, so they are not re-downloaded.
        completed.push(accountType)
        yield call([dbManager, dbManager.setSeedInfo], businessId, {
          seedVersion: SEED_VERSION,
          seededAt: Date.now(),
          completedAccountTypes: completed
        })
      }
    }

    yield put(seedSuccess({ businessId }))
    Mixpanel.track('txn_seed_success', { business_id: businessId, transaction_count: processed })
  } catch (err) {
    const message = err instanceof Error ? err.message : 'seed failed'
    console.error('[seed] Failed to seed business', businessId, err)
    yield put(seedFailure({ businessId, error: message }))
    Mixpanel.track('txn_seed_failure', { business_id: businessId, error: message })
  } finally {
    lock.release()
    inFlightSeeds.delete(businessId)
    if (seedEpoch !== epoch) {
      // Cancelled by signout — wipe anything this run wrote after clearAllData.
      try {
        yield call([dbManager, dbManager.clearAll])
      } catch (clearErr) {
        // best-effort
      }
    }
  }
}

/**
 * Entry saga. Seeds the requested (active) business first — foreground/blocking —
 * then sequentially background-seeds the other owned businesses (never shows UI).
 */
export function* seedTransactionsEffect(
  action: Action<ISeedTriggerPayload>
): Generator<any, void, any> {
  const { businessId, isActive } = action.payload
  if (!businessId) {
    return
  }

  yield call(seedBusiness, businessId, isActive)

  if (isActive) {
    const dashboard = yield select((s: IAppState) => s.Dashboard)
    const ownedIds: string[] = Object.keys(dashboard?.businessAccounts?.businessDetails || {})
    for (const otherId of ownedIds) {
      if (!otherId || otherId === businessId) {
        continue
      }
      yield call(seedBusiness, otherId, false)
    }
  }
}
